import type {SelectChangeEvent} from '@mui/material'
import {Button, Container, List, ListItem, ListItemText, MenuItem, Select, Stack, Typography} from '@mui/material'
import {PageLayout} from '@goschool/components'
import {useUserContext} from '@goschool/auth'
import {useCallback, useEffect, useLayoutEffect, useState} from 'react'
import type {DocumentReference, DocumentSnapshot} from 'firebase/firestore'
import {getDoc} from 'firebase/firestore'
import type {Course, GoSchoolUser} from '@goschool/model'
import {I18nextProvider, Trans, useTranslation} from 'react-i18next'
import {i18nextMsql} from './mslq-i18n'
import {MSLQForm} from './MSLQForm'
import type {MSLQSurveyResponse} from './model'
import {useMSLQDao} from './model'


export function MSLQPage() {
  return <I18nextProvider i18n={i18nextMsql}>
    <PageLayout fullScreen={false}>
      <Container maxWidth="md" sx={{mt: 4}}>
        <MSLQSurveyPage/>
      </Container>
    </PageLayout>
  </I18nextProvider>
}

function MSLQSurveyPage() {

  const [response, setResponse] = useState<DocumentReference<MSLQSurveyResponse>>()

  const {started} = useMSLQDao()


  useLayoutEffect(() => {
    if (started != null && started.length > 0) {
      setResponse(started[0].ref)
    }
  }, [started])

  if (response != null) {
    return <MSLQForm responseReference={response}/>
  }

  return <MSLQStarter response={response} setResponse={setResponse}/>
}

interface MSLQStarterProps {
  response: DocumentReference<MSLQSurveyResponse> | undefined
  setResponse: (response: DocumentReference<MSLQSurveyResponse>) => void
}

function MSLQStarter({response, setResponse}: MSLQStarterProps) {
  const [selectedCourse, setSelectedCourse] = useState<DocumentSnapshot<Course>>()
  const {goSchoolUser} = useUserContext()
  const [courses, setCourses] = useState<DocumentSnapshot<Course>[]>([])
  const {create} = useMSLQDao()
  useEffect(
    () => {
      let unmounted = false
      readCourses(goSchoolUser).then(courses => {
        if (!unmounted) {
          setCourses(courses)
        }
      })

      return () => {
        unmounted = true
      }
    },
    [goSchoolUser]
  )

  useLayoutEffect(() => {
    if (courses.length === 1) {
      const course = courses[0]
      setSelectedCourse(course)
    }
  }, [courses])

  const handleCourseSelect = useCallback(
    (event: SelectChangeEvent) => {
      const selectedCourse = courses.find(course => course.id === event.target.value)
      if (selectedCourse) {
        setSelectedCourse(selectedCourse)
      } else {
        setSelectedCourse(undefined)
      }
    }, [courses, setSelectedCourse])


  const [starting, setStarting] = useState(false)

  const startSurvey = useCallback(
    async () => {
      if (selectedCourse == null) {
        throw new Error('No course selected')
      }
      setStarting(true)
      const response = await create(selectedCourse.ref)
      if (response == null) {
        throw new Error('Failed to start survey')
      }
      setResponse(response)
      setStarting(false)

    }, [create, selectedCourse, setResponse])

  if (courses == null || courses.length === 0) {
    return null
  }

  return <Stack direction="column" spacing={4}>
    <MSLQIntro/>

    <Stack direction="row" alignItems="center" justifyContent="space-between">
      {courses.length > 1 && <Select
        variant="standard"
        size="small"
        onChange={handleCourseSelect}
        value={selectedCourse?.id ?? ''}
        displayEmpty={true}>
        <MenuItem key={-1} value=""><Trans i18nKey="selectACourse"/></MenuItem>
        {courses.map(course =>
          <MenuItem key={course.id} value={course.id}>
            {course.data()?.title ?? 'Unnamed Course'}
          </MenuItem>
        )}
      </Select>}

      <Button variant="contained" loading={starting} onClick={startSurvey} disabled={selectedCourse == null}><Trans
        i18nKey="startSurvey"/></Button>
    </Stack>
  </Stack>
}


async function readCourses(user: GoSchoolUser | null | undefined) {
  if (user == null) {
    return []
  }
  const courseRefs = user.courses ?? []

  return await Promise.all(
    courseRefs.map((courseRef) => getDoc(courseRef))
  )
}


function MSLQIntro() {
  const {t} = useTranslation()

  const bulletPoints = [
    t('mslqIntro.bulletPoints.duration'),
    t('mslqIntro.bulletPoints.noRightOrWrong'),
    t('mslqIntro.bulletPoints.anonymous'),
    t('mslqIntro.bulletPoints.honestAnswers'),
    t('mslqIntro.bulletPoints.voluntary')
  ]

  return <>
    <Typography variant="h5" gutterBottom>
      <Trans i18nKey="mslqIntro.title"/>
    </Typography>

    <Typography variant="body2" gutterBottom={true}>
      <Trans i18nKey="mslqIntro.description"/>
    </Typography>

    <Typography variant="subtitle1">
      <Trans i18nKey="mslqIntro.bulletPointsTitle"/>
    </Typography>

    <List dense>
      {bulletPoints.map((point, index) =>
        <ListItem key={index}>
          <ListItemText primary={point}/>
        </ListItem>
      )}
    </List>

    <Typography variant="body2" color="text.secondary" sx={{my: 3}}>
      <Trans i18nKey="mslqIntro.closing"/>
    </Typography>
  </>
}
